/**
 * Rewrite legacy Joomla internal links (found in migrated article HTML) to the
 * new site's routes. Applied at render time (in RichHtml) so it fixes all
 * migrated content - News, flight logs, static pages - centrally.
 *
 * Legacy link shapes handled:
 *   index.php?option=com_content&...&id=NNN...        -> /flight-logs/NNN (articles are flight logs)
 *   index.php/flying-log/.../NNN-slug[#anchor]        -> /flight-logs/NNN
 *   index.php/news/news-archive/NNN-slug[#anchor]     -> /news/NNN
 *   index.php/sites/wind-rose                          -> /wind-rose
 *   index.php/sites/{alias}                            -> /sites/{siteId}
 *   index.php/gallery/...                              -> /gallery
 *   index.php/contact-us[/...]                         -> /contact
 *   index.php/log-your-flying-here, .../kit-for-sale.. -> /login (member actions)
 * Absolute legacy URLs (http://www.wingbeat-paragliding.co.uk/...) are handled
 * the same way. Genuinely external URLs are left untouched.
 */

// Legacy site alias (Joomla slug) -> our site_id. Our ids are inconsistent, so
// this is an explicit map; unknown aliases fall back to /sites.
const SITE_ALIAS_TO_ID = {
  'black-knowe-head': 'blackknowe',
  bodesbeck: 'bodesbeck',
  bridgend: 'bridgend',
  'cacra-bank': 'cacra',
  'craig-hill': 'craighill',
  eildons: 'eildons',
  'ettrick-pen': 'ettrickpen',
  fastheugh: 'fastheugh',
  foulshiels: 'foulshiels',
  'jubilee-cairn': 'jubileecairn',
  mountbenger: 'mountbenger',
  'newark-hill': 'newark',
  singlie: 'singlie',
  'sundhope-heights': 'sundhope',
  'witchie-knowe': 'witchieknowe',
}

const LEGACY_HOST_RE = /^https?:\/\/(www\.)?wingbeat-paragliding\.co\.uk\//i

/**
 * Map a single href to its new destination, or null if it should be left as-is.
 */
export function rewriteLegacyLink(href) {
  if (!href) return null
  let h = href.replace(/&amp;/g, '&')

  // Strip the legacy host so absolute + relative legacy links share one path.
  const wasLegacyHost = LEGACY_HOST_RE.test(h)
  h = h.replace(LEGACY_HOST_RE, '')

  // Only touch Joomla-style internal links.
  const isInternal = wasLegacyHost || h.startsWith('index.php') || h.startsWith('/index.php')
  if (!isInternal) return null

  h = h.replace(/^\/?index\.php\/?/, '') // drop the index.php prefix
  const [pathPart] = h.split('#')

  // com_content?...&id=NNN -> flight log (migrated articles in catid 12 are logs)
  const q = pathPart.match(/option=com_content.*?[?&]id=(\d+)/)
  if (q) return `/flight-logs/${q[1]}`

  // flying-log/.../NNN-slug
  let m = pathPart.match(/flying-log\/(?:all-flying-logs\/)?(\d+)-/)
  if (m) return `/flight-logs/${m[1]}`
  m = pathPart.match(/^flying-log\/(\d+)-/) // e.g. flying-log/378-...
  if (m) return `/flight-logs/${m[1]}`

  // news/news-archive/NNN-slug
  m = pathPart.match(/news\/(?:news-archive\/)?(\d+)-/)
  if (m) return `/news/${m[1]}`

  // sites/wind-rose
  if (/^sites\/wind-rose\/?$/.test(pathPart)) return '/wind-rose'

  // sites/{alias}
  m = pathPart.match(/^sites\/([a-z0-9-]+)\/?$/i)
  if (m) {
    const id = SITE_ALIAS_TO_ID[m[1].toLowerCase()]
    return id ? `/sites/${id}` : '/sites'
  }
  if (/^sites\/?$/.test(pathPart)) return '/sites'

  // gallery
  if (/^gallery(\/|$)/.test(pathPart)) return '/gallery'

  // contact
  if (/^contact-us(\/|$)/.test(pathPart)) return '/contact'

  // legacy member-only submit forms -> ask to log in
  if (/^(log-your-flying-here|flight-log-image-upload|kit-for-sale|old-kit-spreadsheet)/.test(pathPart)) {
    return '/login'
  }

  // Unknown internal link: send home rather than 404 on the old site.
  return '/'
}
